import { Button, Col, DatePicker, Form, Input, Popconfirm, Radio, Row, Table } from "antd";
import TextArea from "antd/lib/input/TextArea";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { MitigationSectionProps } from ".";
import { DateFormat } from "../../../../../../constants/constants";
import { ControlEffectivenessesModel } from "../../../../../../models/scenario-mitigation.model";
import { AMOUNT_UTIL } from "../../../../../../utils/amount.utils";
import { VALIDATION_UTIL } from "../../../../../../utils/validation.utils";
import { DeleteIcon, EditIcon, InfoCircleIcon, MinusIcon, PlusIcon } from "../../../../../atoms/icon";


const ControlEffectiveness = (props: MitigationSectionProps) => {

    const { t } = useTranslation();

    const columns = useRef([
        {
            title: t("ScenarioMitigation.lbControlName"),
            dataIndex: 'control_name',
            key: 'control_name',
            render: (text: string) => <span className='blue-text'>{text}</span>
        },
        {
            title: t("ScenarioMitigation.lbWeight"),
            dataIndex: 'weight',
            key: 'weight',
            render: (text: number) => AMOUNT_UTIL.convertNumericToFormattedString(text)
        },
        {
            title: t("ScenarioMitigation.lbOverallValue"),
            dataIndex: 'overall_value',
            key: 'overall_value',
            render: (text: number) => AMOUNT_UTIL.convertNumericToFormattedString(text)
        },
        {
            title: t("ScenarioMitigation.lbAction"),
            dataIndex: 'action',
            key: 'action',
            render: (text: string, record: any) => (<div className="action">
                <Button type="text" shape="circle" onClick={() => { edit(record) }}>
                    <EditIcon />
                </Button>
                <Popconfirm 
                title={t("Global.lbConfirmDelete")} 
                onConfirm={() => { deleteRecord(record.id) }}
                okButtonProps={{ shape: "round", style: { padding: '8px 24px' } }}
                cancelButtonProps={{ shape: "round", style: { padding: '8px 24px' } }}>
                    <Button type="text" shape="circle">
                        <DeleteIcon />
                    </Button>
                </Popconfirm>

            </div>)
        }
    ]);

    const addNew = () => {
        if (props.showModal) {
            props.showModal();
        }
    }

    const edit = (record: ControlEffectivenessesModel) => {
        if (props.showModal) {
            props.showModal(record);
        }
    }

    const deleteRecord = (id: number) => {
        if (props.deleteControlEffectivenesses) {
            props.deleteControlEffectivenesses(id);
        }
    }

    return (
        <div className="section-wrapper">
            <div className="table-wrapper">
                <Row>
                    <Col sm={24} className='table-header'>
                        <label>
                            {t("ScenarioMitigation.lbAllEntries")}
                        </label>
                        <Button type="text" onClick={addNew}>
                            + {t("ScenarioMitigation.btnNewEntry")}
                        </Button>
                    </Col>
                </Row>
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Table
                            dataSource={props.data?.control_effectivenesses}
                            columns={columns.current} rowKey="id"
                            pagination={false} />
                    </Col>
                    <Col span={24} className="text-total">
                        <p>{t("ScenarioMitigation.lbTotalEffectivenessValue")}</p><span className="primary-text">{AMOUNT_UTIL.convertNumericToFormattedString(props.data?.total_effectiveness_value)}</span>
                    </Col>
                    <Col span={24} className="text-total">
                        <p>{t("ScenarioMitigation.lbControlEffectiveness")}</p><span className="primary-text"> {props.data?.control_effectiveness_type?.name}
                        </span>
                    </Col>
                </Row>
            </div>

            <Row gutter={[16, 32]}>
                <Col span={24}>
                    <Form.Item
                        label={t("ScenarioMitigation.lbMandatoryMeetingRequired")}
                        name="mandatory_meeting_required"
                        tooltip={{ title: t("ScenarioMitigation.lbMandatoryMeetingRequired"), icon: <InfoCircleIcon /> }}
                    >
                        <Radio.Group>
                            <Radio value={true}>{t("ScenarioMitigation.lbYes")}</Radio>
                            <Radio value={false}>{t("ScenarioMitigation.lbNo")}</Radio>
                        </Radio.Group>
                    </Form.Item>
                </Col>
            </Row>

            <Form.List
                name="review_dates"
            >

                {(fields, { add, remove }, { errors }) => (
                    <div className="form-list">
                        {fields.map((field, index) => (
                            <Form.Item
                                key={field.key}
                                label={index === 0 ?
                                    t("ScenarioMitigation.lbReviewDate") : null}
                                tooltip={index === 0 ? { title: t("ScenarioMitigation.lbReviewDate"), icon: <InfoCircleIcon /> } : null}
                            >
                                <Form.Item
                                    name={[field.name, "id"]}
                                    hidden={true}
                                >
                                    <Input />
                                </Form.Item>
                                <Form.Item
                                    {...field}
                                    name={[field.name, "review_date_picker"]}
                                >
                                    <DatePicker format={DateFormat.view} />
                                </Form.Item>


                                <span className="form-list-action">
                                    <PlusIcon onClick={() => add()} />
                                </span>
                                <span className="form-list-action">
                                    {
                                        index > 0 && <MinusIcon onClick={() => { remove(field.name) }} />
                                    }
                                </span>
                            </Form.Item>
                        ))}

                    </div>
                )}
            </Form.List>


            <Row gutter={[16, 32]}>
                <Col span={24}>
                    <Form.Item
                        label={t("ScenarioMitigation.lbAdditionalNotes")}
                        name="additional_notes"
                        tooltip={{ title: t("ScenarioMitigation.lbAdditionalNotes"), icon: <InfoCircleIcon /> }}
                        rules={VALIDATION_UTIL.getRulesValidationField("additional_notes", props.requiredFields)}
                    >
                        <TextArea rows={5} placeholder={t("ScenarioMitigation.plAdditionalNotes")} />
                    </Form.Item>
                </Col>
            </Row>

        </div>
    )
}

export default ControlEffectiveness;